import { Injectable, inject } from '@angular/core';
import { ENVIRONMENT } from '@cfp/core';
import { DataTableSortOrder } from '@cfp/shared/deprecated/data-table';
import { LabelGroupService } from '@cfp/shared/ui/label-group';
import { BuildingMeasureTableItemDTO, LabelGroupDTO, MeasureStatusDTO } from 'csr-asset-app';
import { BuildingMeasuresOverviewMeasure } from './building-measures-overview-measure';
import { BuildingMeasuresOverviewObjective } from './building-measures-overview-objective';

@Injectable({
  providedIn: 'root'
})
export class BuildingMeasuresService {
  private readonly labelGroupService = inject(LabelGroupService);
  private readonly environment = inject(ENVIRONMENT);

  public filterBuildingMeasures(
    buildingMeasures?: BuildingMeasuresOverviewMeasure[],
    searchText?: string
  ): BuildingMeasuresOverviewMeasure[] {
    if (!buildingMeasures) {
      return [];
    }
    if (this.environment.components?.hideDoneMeasuresAndStatus) {
      buildingMeasures = buildingMeasures.filter(
        (buildingMeasure: BuildingMeasuresOverviewMeasure) => buildingMeasure.status !== MeasureStatusDTO.DONE
      );
    }

    const loweredSearchText = searchText?.toLowerCase();
    if (!loweredSearchText) {
      return buildingMeasures;
    }

    return buildingMeasures.filter((buildingMeasure: BuildingMeasuresOverviewMeasure) =>
      buildingMeasure.searchable.includes(loweredSearchText)
    );
  }

  public filterActiveScenarios(activeScenario: string, buildingMeasures?: BuildingMeasuresOverviewMeasure[]) {
    if (!buildingMeasures) {
      return [];
    }
    switch (activeScenario) {
      case 'planned':
        return [
          ...buildingMeasures.filter(
            (measure: BuildingMeasuresOverviewMeasure) => measure.status === MeasureStatusDTO.PLANNED
          )
        ];
      case 'done':
        return [
          ...buildingMeasures.filter(
            (measure: BuildingMeasuresOverviewMeasure) => measure.status === MeasureStatusDTO.DONE
          )
        ];
      case 'customerMeasures':
        return [...buildingMeasures.filter((measure: BuildingMeasuresOverviewMeasure) => measure.isCustomerMeasure)];
      case 'fiveYear':
        return [
          ...buildingMeasures.filter(
            (measure: BuildingMeasuresOverviewMeasure) =>
              measure.costReduction > 0 && measure.paybackPeriodYears !== undefined && measure.paybackPeriodYears <= 5
          )
        ];
      default:
        return [...buildingMeasures];
    }
  }

  private getImpactOnEnergyLabel(
    measure: BuildingMeasureTableItemDTO,
    objective?: BuildingMeasuresOverviewObjective,
    labelGroup?: LabelGroupDTO,
    labelGroups?: LabelGroupDTO[]
  ): number {
    if (!labelGroup) {
      return 0;
    }

    if (
      objective === BuildingMeasuresOverviewObjective.ImproveEnergyLabel &&
      labelGroup === LabelGroupDTO.CARBONSCORE &&
      labelGroups
    ) {
      const mainLabelGroup = this.labelGroupService.getMainLabelGroup(labelGroups);
      if (!mainLabelGroup) {
        return 0;
      }
      return (
        measure.userDefinedImpactEnergyLabels?.[mainLabelGroup] || measure.impactEnergyLabels?.[mainLabelGroup] || 0
      );
    }
    return measure.userDefinedImpactEnergyLabels?.[labelGroup] || measure.impactEnergyLabels?.[labelGroup] || 0;
  }

  public impactEnergyLabelCompare(
    a: BuildingMeasureTableItemDTO,
    b: BuildingMeasureTableItemDTO,
    sortOrder: DataTableSortOrder | DataTableSortOrder[],
    objective?: BuildingMeasuresOverviewObjective,
    labelGroup?: LabelGroupDTO,
    labelGroups?: LabelGroupDTO[]
  ): number {
    const aImpactEnergyLabel = this.getImpactOnEnergyLabel(a, objective, labelGroup, labelGroups);
    const bImpactEnergyLabel = this.getImpactOnEnergyLabel(b, objective, labelGroup, labelGroups);

    if (aImpactEnergyLabel < bImpactEnergyLabel) {
      return sortOrder === DataTableSortOrder.Ascending ? -1 : 1;
    } else if (aImpactEnergyLabel > bImpactEnergyLabel) {
      return sortOrder === DataTableSortOrder.Ascending ? 1 : -1;
    }

    if (labelGroup === LabelGroupDTO.CARBONSCORE) {
      if (a.co2ConsumptionReduction < b.co2ConsumptionReduction) {
        return sortOrder === DataTableSortOrder.Ascending ? -1 : 1;
      } else if (a.co2ConsumptionReduction > b.co2ConsumptionReduction) {
        return sortOrder === DataTableSortOrder.Ascending ? 1 : -1;
      }
    }

    if (
      a.paybackPeriodYears !== undefined &&
      b.paybackPeriodYears !== undefined &&
      a.paybackPeriodYears < b.paybackPeriodYears
    ) {
      return sortOrder === DataTableSortOrder.Ascending ? -1 : 1;
    } else if (
      a.paybackPeriodYears !== undefined &&
      b.paybackPeriodYears !== undefined &&
      a.paybackPeriodYears > b.paybackPeriodYears
    ) {
      return sortOrder === DataTableSortOrder.Ascending ? 1 : -1;
    }

    return 0;
  }
}
